import React from 'react';
import { Dialog, DialogPanel, DialogTitle } from '@headlessui/react';
import { AlertTriangle, Trash2, X, Loader2 } from 'lucide-react';
import { Button } from '@/src/shadComponents/ui/button';

interface DeleteConfirmDialogProps {
    isOpen: boolean;
    onClose: () => void;
    onConfirm: () => void;
    title: string;
    message: string;
    confirmButtonText?: string;
    isDestructive?: boolean;
    isLoading?: boolean;
}

export default function DeleteConfirmDialog({
    isOpen,
    onClose,
    onConfirm,
    title,
    message,
    confirmButtonText = 'Delete',
    isDestructive = true,
    isLoading = false,
}: DeleteConfirmDialogProps) {
    return (
        <Dialog open={isOpen} onClose={onClose} className="relative z-50">
            {/* Backdrop */}
            <div className="fixed inset-0 bg-black/40 backdrop-blur-sm" />

            {/* Dialog container */}
            <div className="fixed inset-0 flex w-screen items-center justify-center p-4">
                <DialogPanel className="relative max-w-md w-full bg-background rounded-2xl shadow-2xl border border-border overflow-hidden">
                    {/* Close button */}
                    <Button
                        variant="ghost"
                        size="icon"
                        onClick={onClose}
                        disabled={isLoading}
                        className="absolute top-4 right-4"
                    >
                        <X className="h-5 w-5" />
                    </Button>

                    {/* Content */}
                    <div className="p-6 pt-8">
                        {/* Icon and title */}
                        <div className="flex flex-col items-center text-center mb-6">
                            <div
                                className={`flex items-center justify-center w-16 h-16 rounded-full mb-4 ${
                                    isDestructive
                                        ? 'bg-red-100'
                                        : 'bg-amber-100'
                                }`}
                            >
                                {isDestructive ? (
                                    <Trash2 className="h-8 w-8 text-red-600" />
                                ) : (
                                    <AlertTriangle className="h-8 w-8 text-amber-600" />
                                )}
                            </div>
                            <DialogTitle className="text-xl font-semibold text-foreground mb-2">
                                {title}
                            </DialogTitle>
                            <p className="text-sm text-muted-foreground leading-relaxed">
                                {message}
                            </p>
                        </div>

                        {/* Action buttons */}
                        <div className="space-y-3">
                            {/* Primary action - Confirm Delete */}
                            <Button
                                variant="destructive"
                                onClick={onConfirm}
                                disabled={isLoading}
                                className="w-full"
                            >
                                {isLoading ? (
                                    <>
                                        <Loader2 className="h-4 w-4 animate-spin" />
                                        <span>Processing...</span>
                                    </>
                                ) : (
                                    <>
                                        <Trash2 className="h-5 w-5" />
                                        <span>{confirmButtonText}</span>
                                    </>
                                )}
                            </Button>

                            {/* Cancel button */}
                            <Button
                                variant="outline"
                                onClick={onClose}
                                disabled={isLoading}
                                className="w-full"
                            >
                                Cancel
                            </Button>
                        </div>
                    </div>
                </DialogPanel>
            </div>
        </Dialog>
    );
}
